import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchOssCalendar } from "../../api/ossApi";
import { riskLevel } from "../../constants/labels";
import {
  addMonths,
  formatDate,
  groupCalendarItems,
  makeCalendarDays,
  makeCalendarRange,
  mockCalendarItems,
} from "../../utils/calendarUtils";
import { OssCalendarGrid } from "../calendar/OssCalendarGrid";
import { OssSectionPanel } from "../common/OssPanel";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";

export const OssDashboardCalendarPanel = ({ onOpenOrder }) => {
  const { group_code, project_code } = useParams();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const range = useMemo(() => makeCalendarRange(month), [month]);
  const days = useMemo(() => makeCalendarDays(range.start, range.end), [range.start, range.end]);
  const todayKey = formatDate(new Date());
  const { data, loading } = useOssAsyncData({
    fetcher: () =>
      fetchOssCalendar(group_code, project_code, {
        from: formatDate(range.start),
        to: formatDate(range.end),
      }),
    fallbackData: { items: mockCalendarItems },
    errorMessage: "일정 데이터를 불러오지 못했습니다.",
    deps: [group_code, project_code, range.start, range.end],
  });
  const items = data.items?.length ? data.items : mockCalendarItems;
  const grouped = useMemo(() => groupCalendarItems(items), [items]);
  const upcoming = useMemo(
    () =>
      [...items]
        .filter((item) => item.event_date >= todayKey)
        .sort((a, b) => a.event_date.localeCompare(b.event_date))
        .slice(0, 5),
    [items, todayKey]
  );
  const selectedEvents = selectedDay ? grouped.get(selectedDay) || [] : [];

  const openOrder = (orderId) => {
    setOpen(false);
    onOpenOrder(orderId);
  };

  return (
    <>
      <OssSectionPanel title="일정 한눈에 보기">
        <Stack spacing={1}>
          {upcoming.length === 0 ? (
            <Box sx={{ p: 1.2, border: "1px dashed #d6d9e4", borderRadius: "8px", color: "#64748b" }}>
              예정된 일정이 없습니다.
            </Box>
          ) : (
            upcoming.map((item) => {
              const meta = riskLevel[item.risk_level] || riskLevel.NORMAL;
              return (
                <Box
                  key={`${item.event_type}-${item.id}-${item.event_date}`}
                  component="button"
                  type="button"
                  onClick={() => openOrder(item.order_id)}
                  sx={{
                    p: 1,
                    border: `1px solid ${meta.tone}33`,
                    borderRadius: "8px",
                    bgcolor: meta.bg,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                        {item.event_label} · {item.order_no}
                      </Typography>
                      <Typography noWrap sx={{ mt: 0.2, fontSize: 12, color: "#64748b" }}>
                        {item.event_date} · {item.item_name_snapshot}
                      </Typography>
                    </Box>
                    <OpenInNewRoundedIcon sx={{ color: meta.tone, fontSize: 18, flexShrink: 0 }} />
                  </Stack>
                </Box>
              );
            })
          )}
          <Button variant="outlined" startIcon={<CalendarMonthOutlinedIcon />} onClick={() => setOpen(true)}>
            전체 일정 보기
          </Button>
        </Stack>
      </OssSectionPanel>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ px: 2, py: 1.4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthOutlinedIcon sx={{ color: "#6c5ce7" }} />
            <Typography sx={{ fontSize: 20, fontWeight: 900 }}>일정 캘린더</Typography>
          </Stack>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <IconButton onClick={() => setMonth((prev) => addMonths(prev, -1))}>
              <ChevronLeftRoundedIcon />
            </IconButton>
            <Typography sx={{ minWidth: 116, textAlign: "center", fontSize: 17, fontWeight: 800 }}>
              {month.getFullYear()}년 {month.getMonth() + 1}월
            </Typography>
            <IconButton onClick={() => setMonth((prev) => addMonths(prev, 1))}>
              <ChevronRightRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.2, bgcolor: "#f3f6fa" }}>
          <OssCalendarGrid
            days={days}
            grouped={grouped}
            loading={loading}
            month={month}
            onOpenOrder={openOrder}
            onOpenDay={(dayKey, events) => setSelectedDay(events.length ? dayKey : null)}
          />
          {selectedDay && (
            <Box sx={{ mt: 1, p: 1.2, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
              <Typography sx={{ mb: 0.8, fontSize: 15, fontWeight: 900 }}>{selectedDay} 일정</Typography>
              <Stack spacing={0.6}>
                {selectedEvents.map((event) => (
                  <Button
                    key={`${event.event_type}-${event.id}`}
                    variant="outlined"
                    onClick={() => openOrder(event.order_id)}
                    sx={{ justifyContent: "space-between" }}
                  >
                    <span>{event.event_label} · {event.order_no} · {event.item_name_snapshot}</span>
                    <OpenInNewRoundedIcon fontSize="small" />
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
